import { useMemo, useState, useCallback } from "react";
import { CustomTicketV2, EventType, PromoCode } from "@markit/common.types";
import { applyPromoCode } from "./eventUtils/eventUtils";
import {
  getEffectivePriceCents,
  countPaidTickets,
} from "./eventUtils/customTicketUtils";

import {
  isProductSpecificType,
  stripeFixedFee,
  stripeFixedFeeTicketMultiplier,
  stripePercentFee,
} from "@markit/common.utils";
import { testStripe } from "./stripeAccountUtils";

type useFullEventPricesProps = {
  selectedCustomTickets: CustomTicketV2[];
  ticketQuantities: Map<string, number>;
  namedPrices: Map<string, number>;
  tixId: string;
  eventHostId: string;
  eventType: EventType;
  promoCode?: PromoCode;
  creatorPriceId?: string;
  tipAmountCents?: number;
};

export const useFullEventPrices = (props: useFullEventPricesProps) => {
  const {
    selectedCustomTickets,
    tixId,
    promoCode,
    eventHostId,
    eventType,
    ticketQuantities,
    namedPrices,
    creatorPriceId,
    tipAmountCents = 0,
  } = props;

  const [currPromoCode, setCurrPromoCode] = useState(promoCode);
  const [currTixId, setCurrTixId] = useState(tixId);

  const isTestAccount = useMemo(() => testStripe(eventHostId), [eventHostId]);

  const isProductSpecific = useMemo(
    () => isProductSpecificType(eventType),
    [eventType]
  );

  // Price of the base tickets, not including promo or tix id
  const baseTicketPriceCents = useMemo(() => {
    if (currTixId) {
      return 0;
    }

    return selectedCustomTickets.reduce((sum, ticket) => {
      const price = getEffectivePriceCents(ticket, namedPrices);
      return sum + price * (ticketQuantities.get(ticket.id) ?? 0);
    }, 0);
  }, [currTixId, namedPrices, selectedCustomTickets, ticketQuantities]);

  // Price of the base tickets after promo code is applied
  const discountedTicketPriceCents = useMemo(() => {
    return currPromoCode
      ? Math.round(
          applyPromoCode(baseTicketPriceCents, currPromoCode) ??
            baseTicketPriceCents
        )
      : baseTicketPriceCents;
  }, [currPromoCode, baseTicketPriceCents]);

  // Fees for the tickets, not including promo or tix id. Calculated off the ticket price
  // plus any tip, matching the backend (Stripe's own processing fee is charged on the full
  // payment regardless of how it's split) - so this recalculates live as the tip changes.
  // TODO (jonathan): this will use an accountData field in future
  // custom fee for thejoykindler, seaav
  const ticketFeesCents = useMemo(() => {
    const paidTicketsQuantity = countPaidTickets(
      selectedCustomTickets,
      ticketQuantities,
      namedPrices
    );
    // Guard against divide-by-zero when there is no paid ticket quantity.
    if (discountedTicketPriceCents === 0 || paidTicketsQuantity <= 0) {
      return 0;
    }

    const feeBaseCents = discountedTicketPriceCents + tipAmountCents;

    return Math.round(
      feeBaseCents *
        stripePercentFee(
          eventHostId,
          eventType,
          creatorPriceId,
          undefined,
          isTestAccount
        ) +
        stripeFixedFee(
          eventHostId,
          eventType,
          creatorPriceId,
          undefined,
          isTestAccount
        ) *
          stripeFixedFeeTicketMultiplier(eventHostId, paidTicketsQuantity)
    );
  }, [
    creatorPriceId,
    isTestAccount,
    discountedTicketPriceCents,
    eventHostId,
    eventType,
    namedPrices,
    selectedCustomTickets,
    ticketQuantities,
    tipAmountCents,
  ]);

  // Total amount of the tickets, including fees and any tip
  // For products, the fees will be part of the base price (handled in backend)
  const totalAmountCents = useMemo(
    () =>
      Math.round(
        currTixId
          ? 0
          : discountedTicketPriceCents +
              (isProductSpecific ? 0 : ticketFeesCents) +
              tipAmountCents
      ),
    [
      discountedTicketPriceCents,
      ticketFeesCents,
      currTixId,
      isProductSpecific,
      tipAmountCents,
    ]
  );

  const updatePromoCode = useCallback((promoCode: PromoCode | undefined) => {
    setCurrPromoCode(promoCode);
  }, []);

  const updateTixId = useCallback(async (tixId) => {
    setCurrTixId(tixId);
  }, []);

  return {
    baseTicketPriceCents,
    discountedTicketPriceCents,
    ticketFeesCents,
    totalAmountCents,
    currPromoCode,
    updatePromoCode,
    currTixId,
    updateTixId,
  };
};
